// ── 매출 관리 통합 훅 ─────────────────────────────────────────────────────────
// 파인그룹 / DOT / LNJ 등 모든 회사의 매출 관리 상태·핸들러를 하나의 훅으로 처리
// companyCode로 API 경로·권한 코드·테이블 접두사를 동적으로 결정
import { useEffect, useRef, useState, useMemo } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useRevenueLoaders } from './revenueLoaders';

/**
 * 매출 관리 통합 훅
 * @param {string|null} companyCode  — null/'': 파인그룹, 'DOT': DOT, 'LNJ': LNJ, 기타 외부사
 * @param {Function}    showToast    — Toast 알림 함수
 */
export function useRevenue({ companyCode = null, showToast }) {
  // ── API 경로 + 감사 로그 테이블 접두사 계산 ──────────────────────────────────
  // 파인그룹: /api/admin/revenue  / dot_revenue_ prefix 없음
  // DOT:      /api/admin/revenue/dot         / dot_revenue_
  // LNJ 등:   /api/admin/revenue/ext/{code}  / ext_revenue_
  const codeUp    = (companyCode || '').toUpperCase();
  const apiBase   = !companyCode
    ? '/api/admin/revenue'
    : codeUp === 'DOT'
      ? '/api/admin/revenue/dot'
      : `/api/admin/revenue/ext/${companyCode}`;
  const tblPrefix = !companyCode
    ? 'revenue'
    : codeUp === 'DOT'
      ? 'dot_revenue'
      : 'ext_revenue';

  // ── 권한 ──────────────────────────────────────────────────────────────────
  const [canUseSalesManagement,     setCanUseSalesManagement]     = useState(false);
  const [canUseSalesCardManagement, setCanUseSalesCardManagement] = useState(false);

  // ── 탭 ───────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('payments');

  // ── 공용 데이터 ──────────────────────────────────────────────────────────
  const [salesUsers,  setSalesUsers]  = useState([]);
  const [taxInvoices, setTaxInvoices] = useState([]); // 입금 모달 세금계산서 연결용

  // ── 탭별 데이터 ──────────────────────────────────────────────────────────
  const [payments,    setPayments]    = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [phoneApps,   setPhoneApps]   = useState([]);
  const [others,      setOthers]      = useState([]);
  const [cardApps,    setCardApps]    = useState([]);
  const [cardBatches, setCardBatches] = useState([]);
  const [taxBatches,  setTaxBatches]  = useState([]);

  // ── 필터 (기간) ──────────────────────────────────────────────────────────
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd,   setFilterEnd]   = useState('');

  // ── 모달·팝업 상태 ────────────────────────────────────────────────────────
  const [modal,          setModal]          = useState(null);
  const [linkPopup,      setLinkPopup]      = useState(null);
  const [migrationQueue, setMigrationQueue] = useState([]);

  // ── 업로드 상태 ──────────────────────────────────────────────────────────
  const fileRef            = useRef(null);
  const invoiceFileRef     = useRef(null);
  const [uploading,        setUploading]        = useState(false);
  const [invoiceUploading, setInvoiceUploading] = useState(false);

  // ── 권한 조회 ─────────────────────────────────────────────────────────────
  // 파인그룹: SALES_MANAGEMENT / SALES_CARD_MANAGEMENT
  // DOT/외부사: 범용 코드 + 회사별 코드 모두 허용
  useEffect(() => {
    (async () => {
      const r = await apiClient.get('/api/my/permissions');
      const codes   = r.data?.permCodes || [];
      const codeSet = new Set(Array.isArray(codes) ? codes : []);
      // 외부사 전용 추가 권한 코드 목록 (파인그룹은 빈 배열)
      const extraCodes = codeUp
        ? [
            `REVENUE_${codeUp}_MANAGEMENT`,
            `SETTLEMENT_${codeUp}_MANAGEMENT`,
            'REVENUE_DOT_MANAGEMENT',
            'SETTLEMENT_DOT_MANAGEMENT',
          ]
        : [];
      const hasSales = codeSet.has('SALES_MANAGEMENT')      || extraCodes.some((c) => codeSet.has(c));
      const hasCard  = codeSet.has('SALES_CARD_MANAGEMENT') || extraCodes.some((c) => codeSet.has(c));
      setCanUseSalesManagement(hasSales);
      setCanUseSalesCardManagement(hasCard);
      // 카드 전용 권한만 있으면 카드승인 탭으로 이동
      if (!hasSales && hasCard) setTab('cards');
    })();
  }, [codeUp]);

  // ── 데이터 로더 (revenueLoaders.js — apiBase 주입) ───────────────────────
  const {
    loadSalesUsers, loadTaxInvoices, loadPayments, loadInvoices,
    loadPhoneApps, loadOthers, loadCardApps, loadCardBatches, loadTaxBatches,
  } = useRevenueLoaders({
    apiBase,
    filterStart, filterEnd,
    setSalesUsers, setTaxInvoices, setPayments, setInvoices,
    setPhoneApps, setOthers, setCardApps, setCardBatches, setTaxBatches,
  });

  // 초기 로드: 마케터 목록
  useEffect(() => { loadSalesUsers(); }, [loadSalesUsers]);

  // 탭 전환 시 해당 탭 데이터 로드
  useEffect(() => {
    if (tab === 'payments') { loadPayments(); loadTaxInvoices(); }
    if (tab === 'invoices') { loadInvoices(); loadTaxBatches(); }
    if (tab === 'phone')    loadPhoneApps();
    if (tab === 'others')   loadOthers();
    if (tab === 'cards')    { loadCardApps(); loadCardBatches(); }
  }, [tab, loadPayments, loadInvoices, loadPhoneApps, loadOthers, loadCardApps,
      loadCardBatches, loadTaxInvoices, loadTaxBatches]);

  // 단말기ID → 마케터명 맵 (카드승인 탭에서 terminalMap이 필요한 경우 사용)
  const terminalMap = useMemo(() => {
    const map = {};
    for (const u of salesUsers) {
      if (u.terminal_id) map[String(u.terminal_id).trim()] = u.name;
    }
    return map;
  }, [salesUsers]);

  // ── CRUD 핸들러 ───────────────────────────────────────────────────────────

  /** 등록/수정 공통 핸들러 */
  async function handleSave(type, form, id) {
    const isEdit = Boolean(id);
    const url    = isEdit ? `${apiBase}/${type}/${id}` : `${apiBase}/${type}`;
    const method = isEdit ? 'put' : 'post';
    const r = await apiClient[method](url, form);
    if (!r.data?.success) { showToast(r.data?.message || '저장 실패', 'error'); return false; }
    showToast(isEdit ? '수정됐습니다.' : '등록됐습니다.', 'success');
    return r.data;
  }

  /** 삭제 핸들러 */
  async function handleDelete(type, id, label) {
    if (!window.confirm(`${label}을(를) 삭제하시겠습니까?`)) return;
    const r = await apiClient.del(`${apiBase}/${type}/${id}`);
    if (!r.data?.success) { showToast(r.data?.message || '삭제 실패', 'error'); return; }
    showToast('삭제됐습니다.', 'success');
    if (tab === 'payments') loadPayments();
    if (tab === 'invoices') loadInvoices();
    if (tab === 'phone')    loadPhoneApps();
    if (tab === 'others')   loadOthers();
  }

  // ── 배치 삭제/다운로드 핸들러 ─────────────────────────────────────────────

  /** 카드승인 배치 삭제 */
  async function handleCardBatchDelete(batchId) {
    if (!window.confirm('이 배치와 해당 배치로 추가된 카드승인 내역을 삭제하시겠습니까?')) return;
    const r = await apiClient.del(`${apiBase}/card-batches/${batchId}`);
    if (r.data?.success) {
      showToast('배치가 삭제됐습니다.', 'success');
      loadCardBatches(); loadCardApps();
    } else showToast(r.data?.message || '삭제 실패', 'error');
  }

  /** 세금계산서 배치 삭제 */
  async function handleTaxBatchDelete(batchId) {
    if (!window.confirm('이 배치와 해당 배치로 등록된 세금계산서를 삭제하시겠습니까?')) return;
    const r = await apiClient.del(`${apiBase}/tax-invoice-batches/${batchId}`);
    if (r.data?.success) {
      showToast('배치가 삭제됐습니다.', 'success');
      loadTaxInvoices(); loadInvoices();
    } else showToast(r.data?.message || '삭제 실패', 'error');
  }

  /** CSV 다운로드 트리거 (Blob → 링크 클릭)
   *  ※ 서버가 보낸 UTF-8 BOM(EF BB BF)이 response.text()에 의해 제거되므로,
   *     프론트에서 항상 BOM을 다시 붙여야 Excel이 한글을 정상 인식함 */
  function triggerCsvDownload(csvText, fileName) {
    const clean = (csvText || '').replace(/^\uFEFF/, ''); // 혹시 남아있는 BOM 제거 후
    const blob = new Blob(['\uFEFF' + clean], { type: 'text/csv;charset=utf-8' }); // BOM 재삽입
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = (fileName || 'download').replace(/\.[^.]+$/, '') + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  /** 카드승인 배치 CSV 다운로드 */
  async function handleCardBatchDownload(batchId, fileName) {
    const res = await apiClient.get(`${apiBase}/card-batches/${batchId}/download`);
    if (!res.ok) {
      showToast(typeof res.data === 'string' ? res.data : (res.data?.message || '다운로드 실패'), 'error');
      return;
    }
    triggerCsvDownload(res.data, fileName || `card_batch_${batchId}`);
  }

  /** 세금계산서 배치 CSV 다운로드 */
  async function handleTaxBatchDownload(batchId, fileName) {
    const res = await apiClient.get(`${apiBase}/tax-invoice-batches/${batchId}/download`);
    if (!res.ok) {
      showToast(typeof res.data === 'string' ? res.data : (res.data?.message || '다운로드 실패'), 'error');
      return;
    }
    triggerCsvDownload(res.data, fileName || `invoice_batch_${batchId}`);
  }

  // ── 세금계산서 연결 팝업 ──────────────────────────────────────────────────

  /** "미연결" 버튼 클릭 → 버튼 우측에 팝업 위치 계산 */
  function handleLinkClick(e, paymentId) {
    const rect = e.currentTarget.getBoundingClientRect();
    setLinkPopup({ paymentId, top: rect.top, left: rect.right + 6 });
  }

  /** 팝업에서 세금계산서 선택 → 입금에 연결 */
  async function handleLinkInvoice(invoiceId) {
    const payment = payments.find((p) => p.id === linkPopup.paymentId);
    const invoice = taxInvoices.find((ti) => ti.id === invoiceId);
    const payUser = payment?.user_name || null;
    const invUser = invoice?.user_name || null;
    // 마케터 불일치 경고
    if (payUser && invUser && payUser !== invUser) {
      const ok = window.confirm(
        `마케터가 다릅니다.\n\n입금 마케터:       ${payUser}\n세금계산서 마케터: ${invUser}\n\n그래도 연결하시겠습니까?`
      );
      if (!ok) return;
    }
    const r = await apiClient.put(`${apiBase}/payments/${linkPopup.paymentId}`, { tax_invoice_id: invoiceId });
    if (!r.data?.success) { showToast(r.data?.message || '연결 실패', 'error'); return; }
    // 입금 마케터가 있으면 세금계산서 마케터도 동기화
    if (payment?.user_id) {
      await apiClient.put(`${apiBase}/tax-invoices/${invoiceId}`, { user_id: payment.user_id });
    }
    showToast('세금계산서가 연결됐습니다.', 'success');
    setLinkPopup(null);
    loadPayments(); loadTaxInvoices(); loadInvoices();
  }

  // ── 수정 세금계산서 이관 ──────────────────────────────────────────────────

  /** 수정 세금계산서를 이관 모달 큐에 추가 */
  function enqueueMigration(invoices) {
    const list     = Array.isArray(invoices) ? invoices : [invoices];
    const modified = list.filter((inv) => inv?.issueType === '수정' || inv?.issue_type === '수정');
    if (modified.length) setMigrationQueue((q) => [...q, ...modified]);
  }

  /** 현재 큐 맨 앞 처리 완료 → 다음 항목으로 */
  function advanceMigrationQueue() {
    setMigrationQueue((q) => q.slice(1));
  }

  /** 원본 선택 후 이관 실행 */
  async function handleMigratePayments(fromId) {
    const toInvoice = migrationQueue[0];
    const r = await apiClient.post(
      `${apiBase}/tax-invoices/${toInvoice.id}/migrate-payments`,
      { from_invoice_id: fromId }
    );
    if (!r.data?.success) { showToast(r.data?.message || '이관 실패', 'error'); return; }
    showToast(`입금 ${r.data.movedCount}건이 수정본으로 이관됐습니다.`, 'success');
    loadPayments(); loadTaxInvoices(); loadInvoices();
    advanceMigrationQueue();
  }

  // ── 모달 저장 핸들러 ──────────────────────────────────────────────────────

  /** 입력/수정 모달 저장 후 후처리 */
  async function handleModalSave(form) {
    const { type, data } = modal;
    const endpoints = {
      payment: 'payments',
      invoice: 'tax-invoices',
      phone:   'card-phone-approvals',
      other:   'others',
    };
    const result = await handleSave(endpoints[type], form, data?.id);
    if (!result) return;
    setModal(null);

    if (type === 'payment') {
      loadPayments(); loadTaxInvoices();
      // 세금계산서 연결 + 마케터 지정 시 세금계산서 마케터 자동 동기화
      const linkedInvoiceId = form.tax_invoice_id;
      const linkedUserId    = form.user_id;
      if (linkedInvoiceId && linkedUserId) {
        const inv = taxInvoices.find((ti) => String(ti.id) === String(linkedInvoiceId));
        const invUserId = inv?.user_id || null;
        if (invUserId && String(invUserId) !== String(linkedUserId)) {
          const invUserName = inv?.user_name || `#${invUserId}`;
          const payUser     = salesUsers.find((u) => String(u.id) === String(linkedUserId));
          const payUserName = payUser?.name || `#${linkedUserId}`;
          const ok = window.confirm(
            `마케터가 다릅니다.\n\n입금 마케터: ${payUserName}\n세금계산서 마케터: ${invUserName}\n\n세금계산서 마케터를 "${payUserName}"으로 바꾸시겠습니까?`
          );
          if (!ok) return;
        }
        await apiClient.put(`${apiBase}/tax-invoices/${linkedInvoiceId}`, { user_id: linkedUserId });
        loadInvoices();
      }
    }
    if (type === 'invoice') {
      loadInvoices();
      // 수정 세금계산서 신규 등록 시 이관 큐에 추가
      if (!data?.id && result.issueType === '수정') enqueueMigration(result);
    }
    if (type === 'phone') loadPhoneApps();
    if (type === 'other') loadOthers();
  }

  return {
    // 회사 정보
    apiBase, tblPrefix,
    // 권한
    canUseSalesManagement, canUseSalesCardManagement,
    // 탭
    tab, setTab,
    // 공용 데이터
    salesUsers, taxInvoices,
    // 탭별 데이터
    payments, invoices, phoneApps, others, cardApps, cardBatches, taxBatches,
    // 필터
    filterStart, setFilterStart, filterEnd, setFilterEnd,
    // 모달·팝업
    modal, setModal, linkPopup, setLinkPopup, migrationQueue,
    // 업로드
    fileRef, invoiceFileRef, uploading, setUploading, invoiceUploading, setInvoiceUploading,
    // 파생값
    terminalMap,
    // 로더
    loadPayments, loadTaxInvoices, loadInvoices, loadPhoneApps,
    loadOthers, loadCardApps, loadCardBatches, loadTaxBatches,
    // 핸들러
    handleSave, handleDelete,
    handleCardBatchDelete, handleTaxBatchDelete,
    handleCardBatchDownload, handleTaxBatchDownload,
    handleLinkClick, handleLinkInvoice,
    enqueueMigration, advanceMigrationQueue, handleMigratePayments,
    handleModalSave,
  };
}
